import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cloud, CheckCircle2, AlertTriangle, ExternalLink, FolderTree, Upload, FileSpreadsheet, Archive, X, Copy, Download } from "lucide-react";
import { toast } from "sonner";

const REDIRECT_HELP = `Go to https://console.cloud.google.com → Create Project → Enable APIs: "Google Drive API" and "Google Sheets API" → OAuth consent screen (External) → Add test user (yourself) → Credentials → Create OAuth Client ID (Web application) → paste the Redirect URI shown below.`;

const SHEET_KINDS = ["sales", "invoices", "inventory", "livestock", "tasks", "expenses", "customers"];

export default function GoogleDriveTab() {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [backupBusy, setBackupBusy] = useState(false);
    const [backups, setBackups] = useState([]);
    const [uploadCat, setUploadCat] = useState("Photos");
    const [uploadFile, setUploadFile] = useState(null);
    const [params, setParams] = useSearchParams();

    const load = async () => {
        try {
            const r = await api.get("/drive/status");
            setStatus(r.data);
            if (r.data.connected) {
                const b = await api.get("/drive/backups").then(x => x.data);
                setBackups(b);
            }
        } catch (e) {
            toast.error("Failed to load Drive status");
        }
    };
    useEffect(() => { load(); }, []);

    // Detect OAuth return
    useEffect(() => {
        const drive = params.get("drive");
        if (drive === "connected") {
            toast.success("Google Drive connected!");
            params.delete("drive"); setParams(params, { replace: true });
            load();
        } else if (drive === "error") {
            toast.error(`Drive connect failed: ${params.get("msg") || "unknown error"}`);
            params.delete("drive"); params.delete("msg"); setParams(params, { replace: true });
        } else if (drive === "missing_scopes") {
            toast.error("Missing Drive scopes — please re-consent");
            params.delete("drive"); setParams(params, { replace: true });
        }
    }, []);

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
    };

    const connect = async () => {
        setBusy(true);
        try {
            const r = await api.get("/drive/connect");
            window.location.href = r.data.authorization_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Connect failed");
            setBusy(false);
        }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect Google Drive? Files already uploaded will remain in your Drive.")) return;
        await api.post("/drive/disconnect");
        toast.success("Disconnected");
        load();
    };

    const ensureFolders = async () => {
        setBusy(true);
        try {
            const r = await api.post("/drive/folders/ensure");
            toast.success(`Folder tree ready — ${Object.keys(r.data.folders).length} folders`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    const backupNow = async () => {
        setBusy(true);
        try {
            const r = await api.post("/drive/backup");
            toast.success(`Backup uploaded: ${r.data.name}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Backup failed");
        } finally { setBusy(false); }
    };

    const downloadFullBackup = async () => {
        setBackupBusy(true);
        try {
            toast.message("Preparing backup...");
            const response = await api.get("/drive/download-full-backup", { responseType: "blob" });
            const disposition = response.headers?.["content-disposition"] || "";
            const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
            const quoted = disposition.match(/filename="?([^";]+)"?/i)?.[1];
            const now = new Date();
            const pad = value => String(value).padStart(2, "0");
            const fallbackName = `AJ-Farm-Full-Backup-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}.zip`;
            const filename = encoded ? decodeURIComponent(encoded) : (quoted || fallbackName);
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            toast.success("Full backup downloaded");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Unable to prepare the full backup. Please try again.");
        } finally {
            setBackupBusy(false);
        }
    };

    const fullBackupCard = (
        <Card className="p-6" data-testid="full-backup-download-card">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex-1">
                    <h3 className="text-lg font-semibold">{i18n.t("batch4.55d5d2dab9c3")}</h3>
                    <p className="text-sm text-muted-foreground">
                        {i18n.t("batch4.3d05ef2d0a42")}
                    </p>
                </div>
                <Button onClick={downloadFullBackup} disabled={backupBusy} data-testid="download-full-backup-btn">
                    <Download className="w-4 h-4 mr-2" />
                    {backupBusy ? "Preparing backup..." : "Download Full Backup"}
                </Button>
            </div>
        </Card>
    );

    const downloadSource = async () => {
        setBusy(true);
        try {
            toast.message("Packaging your source code... this takes ~20 seconds");
            const r = await api.post("/drive/source-archive");
            toast.success(`Source code uploaded to Drive → Backups (${r.data.size_mb} MB)`);
            if (r.data?.web_view_link) {
                setTimeout(() => window.open(r.data.web_view_link, "_blank"), 500);
            }
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Source archive failed");
        } finally { setBusy(false); }
    };

    const syncSheet = async (kind) => {
        setBusy(true);
        try {
            const r = await api.post(`/drive/sheets/sync/${kind}`);
            toast.success(`Sheet synced — ${r.data.rows} rows`);
            if (r.data.web_link) window.open(r.data.web_link, "_blank");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sync failed");
        } finally { setBusy(false); }
    };

    const doUpload = async () => {
        if (!uploadFile) return;
        setBusy(true);
        try {
            const form = new FormData();
            form.append("file", uploadFile);
            form.append("category", uploadCat);
            const r = await api.post("/drive/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Uploaded to Drive: ${r.data.name}`);
            setUploadFile(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); }
    };

    if (!status) return <div className="p-8 text-center text-muted-foreground">{i18n.t("batch4.b92a577b5dfa")}</div>;

    // ---------- Setup warning (env vars missing) ----------
    if (!status.configured) {
        return (
            <div className="space-y-4">
            <Card className="p-6 border-amber-500/40 bg-amber-500/5" data-testid="drive-setup-card">
                <div className="flex items-start gap-4">
                    <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 space-y-4">
                        <div>
                            <h3 className="text-xl font-bold">{i18n.t("batch4.599e350b1106")}</h3>
                            <p className="text-sm text-muted-foreground mt-1">{i18n.t("batch4.ce63999e7faf")}</p>
                        </div>

                        <ol className="text-sm space-y-3 list-decimal ml-4">
                            <li>{i18n.t("batch4.8a68d90802fa")} <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center gap-1">{i18n.t("batch4.1ad6c18921bf")} <ExternalLink className="w-3 h-3" /></a> {i18n.t("batch4.02727294ed85")}</li>
                            <li>{i18n.t("batch4.2a268b89b83f")} <strong>{i18n.t("batch4.28ba2e1813de")}</strong>{i18n.t("batch4.47fcef440971")}
                                <ul className="list-disc ml-6 mt-1">
                                    <li><code className="bg-secondary px-1.5 py-0.5 rounded text-xs">{i18n.t("batch4.94c903355df3")}</code></li>
                                    <li><code className="bg-secondary px-1.5 py-0.5 rounded text-xs">{i18n.t("batch4.24da69712fb2")}</code></li>
                                </ul>
                            </li>
                            <li>{i18n.t("batch4.ead4b6ef39d4")} <strong>{i18n.t("batch4.ad9bef8ad18e")}</strong> {i18n.t("batch4.fd5b5995a579")}</li>
                            <li>{i18n.t("batch4.2a268b89b83f")} <strong>{i18n.t("batch4.43718b341634")}</strong>{i18n.t("batch4.1b24a75a007b")} <strong>{i18n.t("batch4.8eeb5bd05563")}</strong>.</li>
                            <li>{i18n.t("batch4.8c921c36b6a0")} <strong>{i18n.t("batch4.aea2acee58c1")}</strong>:
                                <div className="mt-2 flex items-center gap-2 bg-card border border-border rounded-lg p-2 font-mono text-xs">
                                    <span className="flex-1 truncate" data-testid="drive-redirect-uri">{status.redirect_uri}</span>
                                    <Button size="sm" variant="outline" onClick={() => copy(status.redirect_uri)} data-testid="copy-redirect-uri"><Copy className="w-3 h-3" /></Button>
                                </div>
                            </li>
                            <li>{i18n.t("batch4.a1c02c6571aa")} <strong>{i18n.t("batch4.a766cd7ff02c")}</strong> {i18n.t("batch4.cffa50a32cb1")} <strong>{i18n.t("batch4.2c4e1babc2d1")}</strong>.</li>
                            <li>{i18n.t("batch4.59a91a7165d2")} <code className="bg-secondary px-1.5 py-0.5 rounded text-xs">{i18n.t("batch4.97ef8b86f35b")}</code>:
                                <pre className="mt-2 bg-card border border-border rounded-lg p-3 text-xs font-mono overflow-x-auto">
{i18n.t("batch4.f2049c0902fc")}{"\n"}
{i18n.t("batch4.dadccfb55b81")}
                                </pre>
                            </li>
                            <li>{i18n.t("batch4.0c62ba6f8de0")}</li>
                        </ol>

                        <div className="bg-card border border-border rounded-lg p-3 text-xs">
                            <div className="font-semibold mb-1">{i18n.t("batch4.22edb691c36c")}</div>
                            <div className="font-mono text-muted-foreground">
                                {status.scopes.map(s => <div key={s}>• {s}</div>)}
                            </div>
                            <div className="mt-2 text-muted-foreground">{i18n.t("batch4.4c96b3f2e11c")}</div>
                        </div>
                    </div>
                </div>
            </Card>
            {fullBackupCard}
            </div>
        );
    }

    // ---------- Not connected (creds present, awaiting OAuth) ----------
    if (!status.connected) {
        return (
            <div className="space-y-4">
            <Card className="p-8 text-center" data-testid="drive-connect-card">
                <Cloud className="w-16 h-16 mx-auto text-primary mb-4" />
                <h3 className="text-2xl font-bold">{i18n.t("batch4.46ebc1883a0c")}</h3>
                <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                    {i18n.t("batch4.0eea57145e81")}
                </p>
                <div className="mt-6 flex justify-center gap-3">
                    <Button onClick={connect} disabled={busy} size="lg" className="h-12" data-testid="drive-connect-btn">
                        <Cloud className="w-5 h-5 mr-2" />{i18n.t("batch4.4a12c0a38a37")}
                    </Button>
                </div>
                <div className="mt-6 text-xs text-muted-foreground">
                    {i18n.t("batch4.1391014ab304")} <span className="font-mono">{i18n.t("batch4.05f2161b418c")}</span> + <span className="font-mono">{i18n.t("batch4.856399932c53")}</span>
                </div>
            </Card>
            {fullBackupCard}
            </div>
        );
    }

    // ---------- Connected ----------
    const driveReady = status.initialized === true;
    const folderProgress = `${status.mapped_folder_count || 0}/${status.expected_folder_count || (status.folder_tree || []).length}`;
    const initializationHelp = "Drive is connected, but its folder mapping is incomplete. Use Ensure Folders before uploads, Sheets sync, or backups.";

    return (
        <div className="space-y-4" data-testid="drive-connected-panel">
            {/* Status card */}
            <Card className="p-6 border-primary/40 bg-primary/5">
                <div className="flex items-center gap-4">
                    <CheckCircle2 className="w-10 h-10 text-primary flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-lg font-bold">{i18n.t("batch4.36a64afce728")}</h3>
                        <p className="text-sm text-muted-foreground">{i18n.t("batch4.ac06ef9ee1f3")} <span className="font-mono">{status.connected_email || "(unknown)"}</span></p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {i18n.t("batch4.db1affa5c28e")} <span className="font-medium">{driveReady ? "Initialized" : "Not initialized"}</span> ({folderProgress} {i18n.t("batch4.31b9b3987172")}
                        </p>
                    </div>
                    <Button variant="outline" onClick={disconnect} data-testid="drive-disconnect-btn"><X className="w-4 h-4 mr-1" />{i18n.t("batch4.ed28e0686e12")}</Button>
                </div>
            </Card>

            {!driveReady && (
                <Card className="p-4 border-amber-500/40 bg-amber-500/5" data-testid="drive-initialization-warning">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-semibold">{i18n.t("batch4.e6f600b0f54a")}</p>
                            <p className="text-sm text-muted-foreground">{initializationHelp}</p>
                        </div>
                    </div>
                </Card>
            )}

            {/* Folder tree */}
            <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                    <FolderTree className="w-5 h-5 text-primary" />
                    <div>
                        <h3 className="text-lg font-semibold">{i18n.t("batch4.0c5ef00834a8")}</h3>
                        <p className="text-xs text-muted-foreground">{i18n.t("batch4.c17097d55a67")} {folderProgress}.</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={ensureFolders} disabled={busy} className="ml-auto" data-testid="drive-ensure-folders-btn">{driveReady ? "Recreate Missing" : "Ensure Folders"}</Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {(status.folder_tree || []).map(name => (
                        <div key={name} className="flex items-center gap-2 p-2 rounded-lg bg-secondary text-sm">
                            <FolderTree className="w-4 h-4 text-primary" />
                            <span className="truncate">{name}</span>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Upload */}
            <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Upload className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold">{i18n.t("batch4.760853ffd22b")}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Label className="tiny-label">{i18n.t("batch4.a3c686e711e4")}</Label>
                        <Select value={uploadCat} onValueChange={setUploadCat} disabled={!driveReady}>
                            <SelectTrigger className="h-11 mt-1" data-testid="drive-upload-cat"><SelectValue /></SelectTrigger>
                            <SelectContent>{(status.folder_tree || []).map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Label className="tiny-label">{i18n.t("batch4.2c3cafa4db3f")}</Label>
                        <Input type="file" disabled={!driveReady} onChange={e => setUploadFile(e.target.files?.[0] || null)} className="h-11 mt-1" data-testid="drive-upload-input" />
                    </div>
                </div>
                {!driveReady && <p className="text-sm text-amber-700 mt-3">{initializationHelp}</p>}
                <Button onClick={doUpload} disabled={!driveReady || !uploadFile || busy} className="mt-3 h-11" data-testid="drive-upload-btn"><Upload className="w-4 h-4 mr-2" />{i18n.t("batch4.8e3ad23b5e66")}</Button>
            </Card>

            {/* Sheets sync */}
            <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                    <FileSpreadsheet className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold">{i18n.t("batch4.d67fc57affe1")}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{driveReady ? "Creates or refreshes a Google Sheet for each data type. Opens the sheet in a new tab." : initializationHelp}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {SHEET_KINDS.map(k => (
                        <Button key={k} variant="outline" onClick={() => syncSheet(k)} disabled={!driveReady || busy} className="h-11 capitalize" data-testid={`drive-sync-${k}-btn`}>
                            <FileSpreadsheet className="w-4 h-4 mr-2" />{k}
                        </Button>
                    ))}
                </div>
            </Card>

            {/* Backup + Source Download */}
            <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Archive className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold">{i18n.t("batch4.780cb0cfb0a2")}</h3>
                    <div className="ml-auto flex flex-wrap justify-end gap-2">
                        <Button size="sm" variant="secondary" onClick={downloadFullBackup} disabled={backupBusy} data-testid="download-full-backup-btn">
                            <Download className="w-4 h-4 mr-1" />{backupBusy ? "Preparing backup..." : "Download Full Backup"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={downloadSource} disabled={!driveReady || busy} data-testid="drive-source-code-btn"><Download className="w-4 h-4 mr-1" />{i18n.t("batch4.0d4f769a368f")}</Button>
                        <Button size="sm" onClick={backupNow} disabled={!driveReady || busy} data-testid="drive-backup-now-btn"><Archive className="w-4 h-4 mr-1" />{i18n.t("batch4.e13d1660c248")}</Button>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                    {driveReady ? <><strong>{i18n.t("batch4.e13d1660c248")}</strong> {i18n.t("batch4.8a653cd514e0")} <strong>{i18n.t("batch4.0d4f769a368f")}</strong> {i18n.t("batch4.cb440b412169")}</> : initializationHelp}
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                    <strong>{i18n.t("batch4.9f4fd4f71409")}</strong> {i18n.t("batch4.a1c4a9e7f224")}
                </p>
                {backups.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">{i18n.t("batch4.2807a1b8372f")}</p>
                ) : (
                    <div className="space-y-2">
                        {backups.slice(0, 10).map(b => (
                            <div key={b.id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary text-sm">
                                <Archive className="w-4 h-4 text-primary" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-mono text-xs truncate">{b.filename}</div>
                                    <div className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleString()} · {(b.size / 1024).toFixed(1)} {i18n.t("batch4.315b39a0fc19")}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}
